import React, { FC, Fragment, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Typography } from '@mui/material';
import { OrderType, SubOrderLineKind } from 'models/order';
import { OrderDetailsContext } from 'pages/order-details/order-details-context';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import RestorationCard from './restoration-card.component';
import SubOrderLinesTable from './sub-order-lines-table.component';

type RestorationsSectionProps = {
    versionCheck: boolean;
    redirectToModifyOrder: (isResend: boolean) => void;
    isExternalTechnician: boolean;
    isCustomer: boolean;
    isSupplier: boolean;
};

/** The Order_1 category `temporaryDentures` maps to this type. */
const TEMPORARY_DENTURE_TYPE = 'TEMPORARY_PARTIAL_DENTURE';

/**
 * Order details render from `order.restorations` for every order shape; changes are
 * highlighted against the original order's restorations. The rows are edited in place
 * through the action and material grids (DD-2506 step 3).
 */
const RestorationsSection: FC<RestorationsSectionProps> = ({
    versionCheck,
    redirectToModifyOrder,
    isExternalTechnician,
    isCustomer,
    isSupplier,
}) => {
    const { t: translate } = useTranslation(['orders']);
    const { order } = useContext(OrderDetailsContext);
    const restorations = order.restorations ?? [];
    const originalRestorations = order.originalOrder?.restorations ?? [];
    // DD-3486: sub-order rows whose restoration matches none of this order's (type and teeth).
    const restorationIds = new Set(restorations.map((restoration) => restoration._id));
    const unmatchedSubOrderLines = (order.subOrderLines ?? []).filter(
        (line) => !line.restorationId || !restorationIds.has(line.restorationId),
    );
    const showSubOrderPrices = !permissions.isRestrictedExternalUser(currentUserVar()?.role);
    const isTemporaryDenture =
        order.type === OrderType.SIMPLE_TYPE &&
        restorations.some((restoration) => restoration.type === TEMPORARY_DENTURE_TYPE);

    return (
        <Fragment>
            {isTemporaryDenture && (
                <Box mb={2}>
                    <Alert severity="info">{translate('orders:TEMP_DENTURE_INFO')}</Alert>
                </Box>
            )}
            {restorations.map((restoration, index) => (
                <RestorationCard
                    key={restoration._id}
                    index={index}
                    restoration={restoration}
                    originalRestoration={originalRestorations[index]}
                    versionCheck={versionCheck}
                    redirectToModifyOrder={redirectToModifyOrder}
                    isExternalTechnician={isExternalTechnician}
                    isCustomer={isCustomer}
                    isSupplier={isSupplier}
                />
            ))}
            {unmatchedSubOrderLines.length ? (
                <Box mb={4} data-test="restorations-section.unmatched-sub-order-lines">
                    <Typography variant="h3">{translate('orders:SUB_ORDER_LINES_UNMATCHED')}</Typography>
                    <SubOrderLinesTable
                        lines={unmatchedSubOrderLines}
                        kind={SubOrderLineKind.ACTION}
                        showPrices={showSubOrderPrices}
                        showRestoration
                    />
                    <SubOrderLinesTable
                        lines={unmatchedSubOrderLines}
                        kind={SubOrderLineKind.MATERIAL}
                        showPrices={showSubOrderPrices}
                        showRestoration
                    />
                </Box>
            ) : undefined}
        </Fragment>
    );
};

export default RestorationsSection;
